import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, ChevronDown, Check, X, Flag, ArrowRight, LifeBuoy } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useTheme } from '../../hooks/useTheme.jsx'
import {
  JOURNEY, APPROVALS, ROLES, CAPABILITY_LABELS, EXTRA_POWERS, SPECIAL, GLOSSARY, KEEPING, KEEPING_RULES,
} from './rolesContent.js'
import { guidesFor } from './guideContent.js'
import HelpTabs from './HelpTabs.jsx'

// Roles & Responsibilities — the page somebody reads on their first day.
//
// It answers four questions in the order a newcomer asks them: who am I here, how does work
// travel from start to finish, who has to approve what, and what do the other roles do. The
// person's own role is opened for them and the rest are one click away, so nobody has to read
// about seven roles to learn about one.
//
// English only for now, like most of the screens that explain the product. The wording lives
// in rolesContent.js so translating it later does not mean touching this file.

function RolesGuide() {
  const { user } = useAuth()
  const { isDark } = useTheme()
  return (
    <RolesGuideView
      role={user?.role}
      isSuperAdmin={!!user?.is_super_admin}
      isMasterPm={!!user?.is_primary_pm}
      capabilities={user?.capabilities || []}
      isDark={isDark}
    />
  )
}

// Everything below takes plain values, so it can be shown for any role without signing in as one.
export function RolesGuideView({ role, isSuperAdmin = false, isMasterPm = false, capabilities = [], isDark = false }) {
  const mine = ROLES.find(r => r.id === role)
  const [openId, setOpenId] = useState(mine?.id || null)

  const c = {
    page: isDark ? 'bg-gray-900' : 'bg-gray-50',
    card: isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
    title: isDark ? 'text-white' : 'text-gray-900',
    body: isDark ? 'text-gray-300' : 'text-gray-700',
    muted: isDark ? 'text-gray-400' : 'text-gray-500',
    soft: isDark ? 'bg-gray-700/60' : 'bg-gray-50',
    line: isDark ? 'border-gray-700' : 'border-gray-200',
    hero: isDark ? 'bg-gray-800 border-blue-500/40' : 'bg-linear-to-r from-blue-50 to-indigo-50 border-blue-200',
    accent: isDark ? 'text-blue-300' : 'text-blue-700',
    link: isDark ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800',
  }
  const h2 = `text-lg font-semibold ${c.title}`

  const holdsEverything = role === 'Admin' || isSuperAdmin
  const heldPowers = capabilities.map(slug => CAPABILITY_LABELS[slug]).filter(Boolean)

  return (
    <div className={`min-h-full p-4 sm:p-6 ${c.page}`}>
      <div className="max-w-5xl mx-auto space-y-8">

        {/* Header */}
        <header className="flex items-start gap-3">
          <div className={`p-2.5 rounded-xl ${isDark ? 'bg-gray-800 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
            <BookOpen className="w-6 h-6" aria-hidden="true" />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${c.title}`}>Roles &amp; Responsibilities</h1>
            <p className={`mt-1 text-sm max-w-3xl ${c.muted}`}>
              Who does what in ProTrack, how work moves from start to finish, and what to do first.
              New here? Read the box below, then the journey, then who approves what.
            </p>
          </div>
        </header>

        <HelpTabs active="/roles" isDark={isDark} showGuide={guidesFor(role, isSuperAdmin).length > 0} />

        {/* 1. You */}
        <section aria-labelledby="you-heading" className={`rounded-2xl border p-5 sm:p-6 ${c.hero}`}>
          <p id="you-heading" className={`text-xs font-semibold uppercase tracking-wider ${c.accent}`}>You are signed in as</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <RoleChip role={role} isDark={isDark} large />
            <span className={`text-lg font-semibold ${c.title}`}>{mine?.label || role || 'Unknown role'}</span>
            {isMasterPm && <Pill isDark={isDark} tone="amber">Master PM: sees every project</Pill>}
            {isSuperAdmin && <Pill isDark={isDark} tone="purple">Super Admin: runs the platform</Pill>}
          </div>

          {mine ? (
            <>
              <p className={`mt-2 ${c.body}`}>{mine.tagline}</p>
              <div className="mt-5 grid gap-6 md:grid-cols-2">
                <div>
                  <h3 className={`text-sm font-semibold ${c.title}`}>Your first steps</h3>
                  <ol className="mt-2 space-y-2">
                    {mine.firstSteps.map((s, i) => (
                      <li key={s.text} className="flex gap-3">
                        <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${isDark ? 'bg-blue-500/20 text-blue-200' : 'bg-blue-600 text-white'}`}>{i + 1}</span>
                        <span className={`text-sm ${c.body}`}>
                          {s.text}{' '}
                          <Link to={s.href} className={`inline-flex items-center gap-1 font-medium underline-offset-2 hover:underline ${c.link}`}>
                            {s.link}<ArrowRight className="w-3 h-3" aria-hidden="true" />
                          </Link>
                        </span>
                      </li>
                    ))}
                  </ol>
                  {guidesFor(role, isSuperAdmin).length > 0 && (
                    <Link to="/roles/guide" className={`mt-3 inline-flex items-center gap-1 text-sm font-medium underline-offset-2 hover:underline ${c.link}`}>
                      See where to click, step by step, with pictures<ArrowRight className="w-3 h-3" aria-hidden="true" />
                    </Link>
                  )}
                </div>
                <div>
                  <h3 className={`text-sm font-semibold ${c.title}`}>Powers on your account</h3>
                  {holdsEverything ? (
                    <p className={`mt-2 text-sm ${c.body}`}>Every extra power. {role === 'Admin' ? 'Admins hold all of them automatically.' : 'A Super Admin holds all of them.'}</p>
                  ) : heldPowers.length > 0 ? (
                    <ul className="mt-2 space-y-1">
                      {heldPowers.map(p => (
                        <li key={p} className={`flex items-start gap-2 text-sm ${c.body}`}>
                          <Check className="mt-0.5 w-4 h-4 shrink-0 text-green-600" aria-hidden="true" />{p}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className={`mt-2 text-sm ${c.body}`}>None beyond your role. If a menu you expect is missing, ask an Admin.</p>
                  )}
                </div>
              </div>
            </>
          ) : (
            <p className={`mt-2 text-sm ${c.body}`}>
              Your role is set by the product you work in, not by ProTrack itself. Ask that product's administrator what it covers.
              You can still read the rest of this page to see how ProTrack works.
            </p>
          )}
        </section>

        {/* 2. Journey */}
        <section aria-labelledby="journey-heading">
          <h2 id="journey-heading" className={h2}>How a piece of work travels</h2>
          <p className={`mt-1 text-sm ${c.muted}`}>From a person joining to a project closing. The people shown are the ones who act at that step.</p>
          <ol className="mt-4">
            {JOURNEY.map((s, i) => (
              <li key={s.id} className="relative flex gap-4 pb-5 last:pb-0">
                {i < JOURNEY.length - 1 && (
                  <span className={`absolute left-4 top-9 bottom-0 w-px ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} aria-hidden="true" />
                )}
                <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">{i + 1}</span>
                <div className={`flex-1 rounded-xl border p-4 ${c.card} ${s.who.includes(role) ? (isDark ? 'ring-2 ring-blue-400/60' : 'ring-2 ring-blue-300') : ''}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className={`font-semibold ${c.title}`}>{s.title}</h3>
                    {s.who.map(w => <RoleChip key={w} role={w} isDark={isDark} />)}
                    {s.who.includes(role) && <span className={`text-xs font-medium ${c.accent}`}>You act here</span>}
                  </div>
                  <p className={`mt-1 text-sm ${c.body}`}>{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* 3. Approvals */}
        <section aria-labelledby="approvals-heading">
          <h2 id="approvals-heading" className={h2}>Who approves what</h2>
          <p className={`mt-1 text-sm ${c.muted}`}>Money is never decided by one person alone: an invoice needs two approvals before it can be paid.</p>
          <div className={`mt-3 overflow-x-auto rounded-xl border ${c.card}`}>
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className={`text-left text-xs uppercase tracking-wide ${c.muted} ${c.soft}`}>
                  <th scope="col" className="px-4 py-3 font-semibold">What</th>
                  <th scope="col" className="px-4 py-3 font-semibold">Who starts it</th>
                  <th scope="col" className="px-4 py-3 font-semibold">Who decides</th>
                  <th scope="col" className="px-4 py-3 font-semibold">Then</th>
                </tr>
              </thead>
              <tbody>
                {APPROVALS.map(a => (
                  <tr key={a.what} className={`border-t ${c.line}`}>
                    <th scope="row" className={`px-4 py-3 text-left font-semibold ${c.title}`}>{a.what}</th>
                    <td className={`px-4 py-3 ${c.body}`}>{a.starts}</td>
                    <td className={`px-4 py-3 ${c.body}`}>{a.decides}</td>
                    <td className={`px-4 py-3 ${c.body}`}>{a.result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* 4. Every role */}
        <section aria-labelledby="roles-heading">
          <h2 id="roles-heading" className={h2}>What each role does</h2>
          <p className={`mt-1 text-sm ${c.muted}`}>Click a role to read what it does and where its limits are.</p>
          <div className="mt-3 space-y-3">
            {ROLES.map(r => {
              const open = openId === r.id
              const isMine = r.id === role
              return (
                <div key={r.id} className={`rounded-xl border ${c.card} ${isMine ? (isDark ? 'ring-2 ring-blue-400/60' : 'ring-2 ring-blue-300') : ''}`}>
                  <button
                    type="button"
                    onClick={() => setOpenId(open ? null : r.id)}
                    aria-expanded={open}
                    aria-controls={`role-${r.id}`}
                    className="flex w-full items-center gap-3 p-4 text-left"
                  >
                    <RoleChip role={r.id} isDark={isDark} />
                    <span className="flex-1">
                      <span className={`block font-semibold ${c.title}`}>
                        {r.label}
                        {isMine && <span className={`ml-2 text-xs font-medium ${c.accent}`}>This is you</span>}
                        {r.paused && <span className={`ml-2 text-xs font-medium ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>Paused</span>}
                      </span>
                      <span className={`block text-sm ${c.muted}`}>{r.tagline}</span>
                    </span>
                    <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${c.muted} ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>
                  {open && (
                    <div id={`role-${r.id}`} className={`grid gap-6 border-t px-4 pb-5 pt-4 md:grid-cols-2 ${c.line}`}>
                      <div>
                        <h3 className={`text-sm font-semibold ${c.title}`}>What you do</h3>
                        <ul className="mt-2 space-y-1.5">
                          {r.youDo.map(t => (
                            <li key={t} className={`flex items-start gap-2 text-sm ${c.body}`}>
                              <Check className="mt-0.5 w-4 h-4 shrink-0 text-green-600" aria-hidden="true" />{t}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h3 className={`text-sm font-semibold ${c.title}`}>Good to know</h3>
                        <ul className="mt-2 space-y-1.5">
                          {r.notYours.map(t => (
                            <li key={t} className={`flex items-start gap-2 text-sm ${c.body}`}>
                              <X className="mt-0.5 w-4 h-4 shrink-0 text-gray-400" aria-hidden="true" />{t}
                            </li>
                          ))}
                        </ul>
                        <h3 className={`mt-4 text-sm font-semibold ${c.title}`}>Where you work</h3>
                        <p className={`mt-1 text-sm ${c.body}`}>{r.screens.join(' · ')}</p>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </section>

        {/* 5. Extra powers */}
        <section aria-labelledby="powers-heading">
          <h2 id="powers-heading" className={h2}>How people get extra powers</h2>
          <p className={`mt-1 text-sm ${c.muted}`}>Two people with the same role can see different menus. That is normal: authority comes in three ways.</p>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            {EXTRA_POWERS.map((p, i) => (
              <div key={p.title} className={`rounded-xl border p-4 ${c.card}`}>
                <span className={`text-xs font-bold ${c.accent}`}>WAY {i + 1}</span>
                <h3 className={`mt-1 font-semibold ${c.title}`}>{p.title}</h3>
                <p className={`mt-1 text-sm ${c.body}`}>{p.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            {SPECIAL.map(p => (
              <div key={p.title} className={`rounded-xl border p-4 ${c.card}`}>
                <h3 className={`flex items-center gap-2 font-semibold ${c.title}`}><Flag className="w-4 h-4 text-amber-500" aria-hidden="true" />{p.title}</h3>
                <p className={`mt-1 text-sm ${c.body}`}>{p.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How long things are kept */}
        <section aria-labelledby="keeping-heading">
          <h2 id="keeping-heading" className={h2}>How long things are kept</h2>
          <p className={`mt-1 text-sm ${c.muted}`}>These are the standard periods. A company can set its own for the first three; a consultant's own files follow the same year everywhere.</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {KEEPING.map(k => (
              <div key={k.what} className={`rounded-xl border p-4 ${c.card}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className={`font-semibold ${c.title}`}>{k.what}</h3>
                  <span className={`text-sm font-bold ${c.accent}`}>{k.period}</span>
                </div>
                <p className={`mt-1 text-sm ${c.body}`}>{k.text}</p>
              </div>
            ))}
          </div>
          <ul className={`mt-3 list-disc pl-5 space-y-1 text-sm ${c.body}`}>
            {KEEPING_RULES.map(r => <li key={r}>{r}</li>)}
          </ul>
        </section>

        {/* 6. Glossary */}
        <section aria-labelledby="glossary-heading">
          <h2 id="glossary-heading" className={h2}>Words you will see</h2>
          <dl className="mt-3 grid gap-3 sm:grid-cols-2">
            {GLOSSARY.map(g => (
              <div key={g.term} className={`rounded-xl border p-4 ${c.card}`}>
                <dt className={`font-semibold ${c.title}`}>{g.term}</dt>
                <dd className={`mt-1 text-sm ${c.body}`}>{g.text}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* 7. Help */}
        <section className={`flex flex-wrap items-center gap-3 rounded-xl border p-4 ${c.card}`}>
          <LifeBuoy className={`w-5 h-5 ${c.accent}`} aria-hidden="true" />
          <p className={`flex-1 text-sm ${c.body}`}>
            Something unclear, or not working as described here? Report it with the feedback button, and follow what happens to it in My Tickets.
          </p>
          <Link to="/my-tickets" className={`text-sm font-medium underline-offset-2 hover:underline ${c.link}`}>Open My Tickets</Link>
        </section>

      </div>
    </div>
  )
}

// A role as a coloured chip. The shared RoleBadge leans on stylesheet classes that are not
// defined anywhere, so most roles show as bare text there; this page needs the colour to tell
// seven roles apart at a glance, so it carries its own.
const CHIP = {
  'Admin': ['bg-rose-100 text-rose-800', 'bg-rose-500/20 text-rose-200', 'Admin'],
  'Project Manager': ['bg-blue-100 text-blue-800', 'bg-blue-500/20 text-blue-200', 'PM'],
  'Finance': ['bg-emerald-100 text-emerald-800', 'bg-emerald-500/20 text-emerald-200', 'Finance'],
  'Consultant': ['bg-indigo-100 text-indigo-800', 'bg-indigo-500/20 text-indigo-200', 'Consultant'],
  'HR': ['bg-purple-100 text-purple-800', 'bg-purple-500/20 text-purple-200', 'HR'],
  'Funder': ['bg-amber-100 text-amber-800', 'bg-amber-500/20 text-amber-200', 'Funder'],
  'Partner': ['bg-gray-200 text-gray-800', 'bg-gray-500/30 text-gray-200', 'Partner'],
}

function RoleChip({ role, isDark, large = false }) {
  const [light, dark, short] = CHIP[role] || ['bg-gray-100 text-gray-800', 'bg-gray-500/30 text-gray-200', role || 'User']
  const size = large ? 'px-3 py-1.5 text-sm' : 'px-2 py-0.5 text-xs'
  return <span className={`inline-flex items-center rounded-full font-semibold ${size} ${isDark ? dark : light}`}>{short}</span>
}

function Pill({ children, tone, isDark }) {
  const tones = {
    amber: isDark ? 'bg-amber-500/20 text-amber-200' : 'bg-amber-100 text-amber-800',
    purple: isDark ? 'bg-purple-500/20 text-purple-200' : 'bg-purple-100 text-purple-800',
  }
  return <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${tones[tone]}`}>{children}</span>
}

export default RolesGuide
